// MAP HAZARDS (owner 2026-11-02: 'LIGHT gameplay effects, always telegraphed and fair' — lava cracks burn enemies AND the
// player a little, dark ice is slippery, sandstorms cut visibility, collapsing set pieces crush whoever is under them).
// A hazard is a zone with a timeline: idle → WARN (telegraph, nothing happens yet) → ACTIVE (the effect) → idle again
// (repeating) or done. Every hazard has a warning phase of at least HAZARD_MIN_WARN, so nothing ever hits unannounced.
// The same rules apply to the player and to enemies (unless a zone is restricted with `who`).
// Pure + deterministic: zones come from the map's generator (seeded Rng), time advances in fixed DT steps. No three/DOM.

export type HazardKind = 'burn' | 'slow' | 'slip' | 'fog' | 'crush';
export const HAZARD_KINDS: readonly HazardKind[] = ['burn', 'slow', 'slip', 'fog', 'crush'];
/** Shortest telegraph per kind (s). Crush is the only one that can hurt in one go, so it gets the longest warning. */
export const HAZARD_MIN_WARN: Record<HazardKind, number> = { burn: 1, slow: 0.5, slip: 0.5, fog: 2, crush: 1.6 };
/** Burn on enemies: share of their max health lost per second per point of power (power = player HP/s). */
export const HAZARD_ENEMY_BURN = 0.015;
/** Bosses and guardians feel hazards at this share (burn) / take this share of max health from a crush. */
export const HAZARD_BOSS_BURN = 0.25, HAZARD_BOSS_CRUSH = 0.06;
/** Fog also slows whoever is inside it a little (they cannot see either): speed x (1 - FOG_SLOW * power). */
export const HAZARD_FOG_SLOW = 0.12;
/** Caps that keep hazards 'light' whatever a map asks for. */
export const HAZARD_CAP = { burn: 8, slowMin: 0.45, slip: 1, fog: 0.75, crush: 45 } as const;

export interface HazardDef {
  kind: HazardKind;
  /** centre (m) */
  x: number; z: number;
  /** circle radius (m); ignored for rectangles and global zones */
  r: number;
  /** oriented rectangle instead of a circle: half width (across) and half length (along `rot`, radians from +x) */
  w?: number; l?: number; rot?: number;
  /** whole arena (storms, ash fall): no position test */
  global?: boolean;
  /** run time (s) when the first warning starts */
  start: number;
  /** telegraph length (s); raised to HAZARD_MIN_WARN */
  warn: number;
  /** effect length (s); Infinity = stays on once active; crush acts once at the end of the warning (use 0) */
  active: number;
  /** repeat period (s, start to start); omit = once */
  every?: number;
  /** burn: player HP per second (enemies: HAZARD_ENEMY_BURN x power of their max HP / s); slow: speed multiplier (0.45..1);
   *  slip: 0..1 grip lost; fog: 0..0.75 visibility lost; crush: player damage (non-boss foes are crushed outright) */
  power: number;
  /** who it affects (default both) */
  who?: 'all' | 'player' | 'enemies';
  /** crush: leave a crater (default true) */
  crater?: boolean;
  /** free render key: which set piece / telegraph style the map's renderer draws for this zone */
  tag?: string;
}

export const HZ = { idle: 0, warn: 1, active: 2, done: 3 } as const;
export type HazardPhase = typeof HZ[keyof typeof HZ];

export interface HazardZone extends HazardDef {
  id: number;
  phase: HazardPhase;
  /** seconds spent in the current phase */
  t: number;
  /** run time of the next warning start (idle zones) */
  next: number;
  /** completed activations */
  count: number;
}

export type HazardEvent = { t: 'hazard'; id: number; kind: HazardKind; phase: 'warn' | 'active' | 'fire' | 'end'; x: number; z: number; r: number };
export interface HazardSink { push(e: HazardEvent): unknown }

const clampPower = (d: HazardDef): number => {
  switch (d.kind) {
    case 'burn': return Math.min(HAZARD_CAP.burn, Math.max(0, d.power));
    case 'slow': return Math.min(1, Math.max(HAZARD_CAP.slowMin, d.power));
    case 'slip': return Math.min(HAZARD_CAP.slip, Math.max(0, d.power));
    case 'fog': return Math.min(HAZARD_CAP.fog, Math.max(0, d.power));
    case 'crush': return Math.min(HAZARD_CAP.crush, Math.max(0, d.power));
  }
};

export class HazardField {
  readonly zones: HazardZone[];
  /** zones in their ACTIVE phase this tick (queries only look at these) */
  readonly live: HazardZone[] = [];
  /** crush zones that went off this tick (the Sim resolves them) */
  readonly fired: HazardZone[] = [];

  constructor(defs: readonly HazardDef[]) {
    this.zones = defs.map((d, i) => ({
      ...d, power: clampPower(d), warn: Math.max(HAZARD_MIN_WARN[d.kind], d.warn), active: d.kind === 'crush' ? 0 : Math.max(0, d.active),
      start: Math.max(0, d.start), id: i + 1, phase: HZ.idle, t: 0, next: Math.max(0, d.start), count: 0,
    }));
  }

  get size(): number { return this.zones.length; }

  /** Advance every timeline by dt (call once per sim tick, with the run time after the step). */
  update(time: number, dt: number, out: HazardSink): void {
    this.fired.length = 0;
    if (!this.zones.length) return;
    this.live.length = 0;
    for (const z of this.zones) {
      if (z.phase === HZ.done) continue;
      if (z.phase === HZ.idle) {
        if (time < z.next) continue;
        z.phase = HZ.warn; z.t = 0; this.emit(z, 'warn', out);
      } else z.t += dt;
      if (z.phase === HZ.warn && z.t >= z.warn) {
        z.phase = HZ.active; z.t = 0;
        if (z.kind === 'crush') { this.fired.push(z); this.emit(z, 'fire', out); } else this.emit(z, 'active', out);
      }
      if (z.phase === HZ.active && z.t >= z.active) {
        z.count++;
        this.emit(z, 'end', out);
        if (z.every && z.every > 0) { z.phase = HZ.idle; z.next = Math.max(time + 1e-6, z.start + z.count * Math.max(z.every, z.warn + z.active + 0.5)); z.t = 0; }
        else z.phase = HZ.done;
      }
      if (z.phase === HZ.active && z.kind !== 'crush') this.live.push(z);
    }
  }

  private emit(z: HazardZone, phase: HazardEvent['phase'], out: HazardSink): void {
    out.push({ t: 'hazard', id: z.id, kind: z.kind, phase, x: z.x, z: z.z, r: z.global ? 1e4 : z.l ? Math.hypot(z.w ?? 0, z.l) : z.r });
  }

  /** Is (x, z) inside the zone's footprint (pad = extra margin, e.g. a body radius)? */
  static contains(h: HazardDef, x: number, z: number, pad = 0): boolean {
    if (h.global) return true;
    const dx = x - h.x, dz = z - h.z;
    if (h.l) {
      const c = Math.cos(h.rot ?? 0), s = Math.sin(h.rot ?? 0);
      return Math.abs(dx * c + dz * s) <= h.l + pad && Math.abs(-dx * s + dz * c) <= (h.w ?? 0) + pad;
    }
    return dx * dx + dz * dz <= (h.r + pad) * (h.r + pad);
  }
  /** bounding radius (for spatial queries) */
  static reach(h: HazardDef): number { return h.global ? 1e4 : h.l ? Math.hypot(h.w ?? 0, h.l) : h.r; }

  private static hits(h: HazardDef, enemy: boolean): boolean { const w = h.who ?? 'all'; return w === 'all' || (enemy ? w === 'enemies' : w === 'player'); }

  /** Speed multiplier at (x, z) from active slow / fog zones (1 = no effect). */
  moveMul(x: number, z: number, enemy: boolean): number {
    let m = 1;
    for (const h of this.live) {
      if ((h.kind !== 'slow' && h.kind !== 'fog') || !HazardField.hits(h, enemy) || !HazardField.contains(h, x, z)) continue;
      m *= h.kind === 'slow' ? h.power : 1 - HAZARD_FOG_SLOW * h.power;
    }
    return m;
  }
  /** Grip lost at (x, z) from active slip zones (0 = full grip .. 1). */
  slip(x: number, z: number, enemy: boolean): number {
    let s = 0;
    for (const h of this.live) if (h.kind === 'slip' && HazardField.hits(h, enemy) && HazardField.contains(h, x, z)) s = Math.max(s, h.power);
    return s;
  }
  /** Burn power at (x, z) (player HP / s). */
  burn(x: number, z: number, enemy: boolean): number {
    let b = 0;
    for (const h of this.live) if (h.kind === 'burn' && HazardField.hits(h, enemy) && HazardField.contains(h, x, z)) b += h.power;
    return Math.min(HAZARD_CAP.burn, b);
  }
  /** Visibility at (x, z): 1 = clear .. 0.25 (render fog follows it). */
  visibility(x: number, z: number): number {
    let v = 1;
    for (const h of this.live) if (h.kind === 'fog' && HazardField.contains(h, x, z)) v = Math.min(v, 1 - h.power);
    return v;
  }
  /** Telegraph progress 0..1 of a warning zone (render helper). */
  static warnK(h: HazardZone): number { return h.phase === HZ.warn ? Math.min(1, h.t / h.warn) : h.phase === HZ.active ? 1 : 0; }
}
